<template>
  <div>
    <div class="flex flex-col relative">
      <label
        v-if="label"
        class="mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >{{ label }}</label
      >
      <button
        v-if="showCategoryDropdown"
        :id="`${inputId}-category-btn`"
        ref="categoryButton"
        type="button"
        class="shrink-0 z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-gray-900 bg-gray-100 border-t border-l border-b border-gray-300 rounded-s-lg hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white dark:border-gray-600"
        @click="toggleCategoryDropdown"
      >
        {{ selectedCategory }}
        <svg
          v-if="!isCategoryDropdownOpen"
          class="w-2.5 h-2.5 ms-2.5 flex-shrink-0"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m1 1 4 4 4-4"
          />
        </svg>
        <svg
          v-else
          class="w-2.5 h-2.5 ms-2.5 flex-shrink-0"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m1 5 4-4 4 4"
          />
        </svg>
      </button>

      <div
        v-show="isCategoryDropdownOpen"
        :id="`${inputId}-category-dropdown`"
        ref="categoryDropdown"
        class="absolute bg-white border border-gray-300 rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600"
        style="top: 45px"
      >
        <ul
          class="py-0 text-sm text-gray-700 dark:text-gray-200"
          :aria-labelledby="`${inputId}-category-btn`"
        >
          <li v-for="(category, index) in categories" :key="index">
            <button
              type="button"
              class="inline-flex w-full px-4 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
              @click="selectCategory(category)"
            >
              {{ category }}
            </button>
          </li>
        </ul>
      </div>

      <div class="relative w-full" :class="{ flex: showFilterDropdown }">
        <SearchIcon
          class="h-[14px] absolute left-[10px] top-[14px] text-gray-500"
          :class="{ 'text-red-700': validation === 'error' }"
        />
        <input
          :id="inputId"
          type="text"
          class="block p-2.5 pl-7 w-full z-20 text-sm text-gray-900 bg-gray-50 border -l border-gray-300 border-t border-b border-r dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white focus:outline-none focus:ring-0 search-input"
          :class="[
            {
              'rounded-e-lg': !showFilterDropdown && !showCategoryDropdown,
              ' rounded-e-lg ': showCategoryDropdown && !showFilterDropdown,
              'rounded-s-lg ': !showCategoryDropdown,
              'pr-10': showClearButton /* Always apply padding */
            },
            inputClasses
          ]"
          :style="{ minWidth: '300px' }"
          :placeholder="placeholder"
          :value="searchQuery"
          @input="updateSearchQuery"
        />

        <button
          v-if="showClearButton && searchQuery"
          type="button"
          class="absolute z-10 cursor-pointer flex items-center justify-center"
          :style="{
            top: '50%',
            right: showFilterDropdown || showSearchButton ? '50px' : '20px',
            transform: 'translateY(-50%)'
          }"
          @click="clearSearch"
        >
          <svg
            class="w-4 h-4 text-gray-500 dark:text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <button
          v-if="showFilterDropdown"
          :id="`${inputId}-filter-btn`"
          ref="filterButton"
          type="button"
          class="z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-gray-900 bg-gray-100 border-t border-b border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white dark:border-gray-600 whitespace-nowrap"
          @click="toggleFilterDropdown"
        >
          {{ filterText }}
          <svg
            v-if="!isFilterDropdownOpen"
            class="w-2.5 h-2.5 ms-2.5 flex-shrink-0"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 4 4 4-4"
            />
          </svg>
          <svg
            v-else
            class="w-2.5 h-2.5 ms-2.5 flex-shrink-0"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 5 4-4 4 4"
            />
          </svg>
        </button>

        <div
          v-show="isFilterDropdownOpen"
          :id="`${inputId}-filter-dropdown`"
          ref="filterDropdown"
          class="absolute bg-white border border-gray-300 rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600"
          style="top: 45px; right: 35px"
        >
          <ul
            class="py-0 text-sm text-gray-700 dark:text-gray-200"
            :aria-labelledby="`${inputId}-filter-btn`"
          >
            <li v-for="(option, index) in filterOptions" :key="index">
              <button
                type="button"
                class="inline-flex w-full px-4 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                @click="selectFilter(option)"
              >
                {{ option }}
              </button>
            </li>
          </ul>
        </div>

        <button
          v-if="showSearchButton"
          type="submit"
          class="absolute top-0 right-0 p-2.5 text-sm font-medium h-full text-white bg-blue-700 rounded-e-lg border border-blue-700 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700"
          :class="{ relative: showFilterDropdown }"
        >
          <svg
            class="w-4 h-4"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 20 20"
          >
            <path
              stroke="white"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
            />
          </svg>
          <span class="sr-only">Search</span>
        </button>
      </div>
    </div>
    <!-- Helper Text -->
    <p
      v-if="helperText"
      :id="`${inputId}-helper-text`"
      :class="helperTextClasses"
    >
      <span v-if="validation === 'success'" class="font-medium">Alright!</span>
      <span v-if="validation === 'error'" class="font-medium">Oops!</span>
      {{ helperText }}
    </p>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import SearchIcon from "@/images/search.svg?component";

const props = defineProps({
  // Optional v-model. Left null by default so the existing consumers, which
  // only listen to update:searchQuery, keep owning the value internally — a
  // parent that needs to clear or preset the box passes this instead.
  modelValue: {
    type: String,
    default: null
  },
  placeholder: {
    type: String,
    default: "Search..."
  },
  label: {
    type: String,
    default: "Search"
  },
  inputId: {
    type: String,
    default: "search-input"
  },
  showCategoryDropdown: {
    type: Boolean,
    default: false
  },
  showFilterDropdown: {
    type: Boolean,
    default: false
  },
  showSearchButton: {
    type: Boolean,
    default: false
  },
  showClearButton: {
    type: Boolean,
    default: false
  },
  categories: {
    type: Array,
    default: () => ["All categories", "Mockups", "Templates", "Design", "Logos"]
  },
  filterOptions: {
    type: Array,
    default: () => ["Filter", "Option 1", "Option 2", "Option 3"]
  },
  validation: {
    type: String,
    default: ""
  },
  helperText: {
    type: String,
    default: ""
  }
});

const emit = defineEmits([
  "update:modelValue",
  "update:searchQuery",
  "search",
  "categoryChange",
  "filterChange"
]);

const searchQuery = ref(props.modelValue ?? "");

watch(
  () => props.modelValue,
  (value) => {
    if (value !== null && value !== searchQuery.value)
      searchQuery.value = value;
  }
);

function setQuery(value) {
  searchQuery.value = value;
  emit("update:modelValue", value);
  emit("update:searchQuery", value);
}
const isCategoryDropdownOpen = ref(false);
const isFilterDropdownOpen = ref(false);
const selectedCategory = ref(props.categories[0]);
const selectedFilter = ref(props.filterOptions[0]);

const categoryButton = ref(null);
const categoryDropdown = ref(null);
const filterButton = ref(null);
const filterDropdown = ref(null);

const filterText = computed(() => selectedFilter.value);

function toggleCategoryDropdown(event) {
  event.stopPropagation();
  isCategoryDropdownOpen.value = !isCategoryDropdownOpen.value;
  if (isCategoryDropdownOpen.value) {
    isFilterDropdownOpen.value = false;
  }
  if (isCategoryDropdownOpen.value) {
    nextTick(() => {});
  }
}

function toggleFilterDropdown(event) {
  event.stopPropagation();
  isFilterDropdownOpen.value = !isFilterDropdownOpen.value;
  if (isFilterDropdownOpen.value) {
    isCategoryDropdownOpen.value = false;
  }
  if (isFilterDropdownOpen.value) {
    nextTick(() => {});
  }
}

function selectCategory(category) {
  selectedCategory.value = category;
  isCategoryDropdownOpen.value = false;
  emit("categoryChange", category);
}

function selectFilter(option) {
  selectedFilter.value = option;
  isFilterDropdownOpen.value = false;
  emit("filterChange", option);
}

const clearSearch = () => {
  setQuery("");
  const inputElement = document.getElementById(props.inputId);
  if (inputElement) {
    inputElement.focus();
  }
};

function handleSubmit() {
  emit("search", {
    query: searchQuery.value,
    category: selectedCategory.value,
    filter: selectedFilter.value
  });
}

function updateSearchQuery(event) {
  setQuery(event.target.value);
}

const helperTextClasses = computed(() => {
  const sizeClass =
    props.size === "small"
      ? "text-xs"
      : props.size === "large"
        ? "text-base"
        : "text-sm";

  const baseClasses = ["mt-2", sizeClass];

  if (props.validation === "success") {
    baseClasses.push("text-green-600 dark:text-green-500");
  } else if (props.validation === "error") {
    baseClasses.push("text-red-600 dark:text-red-500");
  } else if (props.disabled) {
    baseClasses.push("text-gray-500 dark:text-gray-400");
  } else {
    baseClasses.push("text-gray-500 dark:text-gray-400");
  }

  return baseClasses.join(" ");
});

// Computed class for input
const inputClasses = computed(() => {
  const baseClasses = [
    "bg-gray-50 rounded-lg border block w-full dark:bg-gray-700 dark:text-white dark:border-gray-600",
    props.size === "large"
      ? "p-4 text-base"
      : props.size === "small"
        ? "p-2 text-xs"
        : "p-2.5 text-sm" // Default to base size
  ];

  if (props.validation === "success") {
    baseClasses.push(
      "bg-green-50 border-green-500 text-green-900 placeholder-green-700 focus:ring-green-500 focus:border-green-500 dark:text-green-400 dark:border-green-500 dark:placeholder-green-500"
    );
  } else if (props.validation === "error") {
    baseClasses.push(
      "bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500"
    );
  } else if (props.disabled) {
    baseClasses.push(
      "bg-gray-200 text-gray-400 border-gray-300 placeholder-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-500 dark:border-gray-500 dark:placeholder-gray-600"
    );
  } else {
    baseClasses.push(
      "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 dark:placeholder-gray-400 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
    );
  }

  if (props.icon) {
    baseClasses.push("pl-10");
  } else {
    baseClasses.push("pl-3");
  }

  return baseClasses.join(" ");
});

function closeDropdowns(event) {
  const isClickInCategoryButton =
    categoryButton.value && categoryButton.value.contains(event.target);
  const isClickInFilterButton =
    filterButton.value && filterButton.value.contains(event.target);
  const isClickInCategoryDropdown =
    categoryDropdown.value && categoryDropdown.value.contains(event.target);
  const isClickInFilterDropdown =
    filterDropdown.value && filterDropdown.value.contains(event.target);

  if (
    isCategoryDropdownOpen.value &&
    !isClickInCategoryButton &&
    !isClickInCategoryDropdown
  ) {
    isCategoryDropdownOpen.value = false;
  }

  if (
    isFilterDropdownOpen.value &&
    !isClickInFilterButton &&
    !isClickInFilterDropdown
  ) {
    isFilterDropdownOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", closeDropdowns);
});

onUnmounted(() => {
  document.removeEventListener("click", closeDropdowns);
});
</script>

<style scoped></style>
